import React, { useEffect, useState } from 'react';
import "./Invoices.css";
import { Button, DatePicker, Form, Input, InputNumber, message, Modal, Select, Space, Table, Upload } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { collection, doc, getDocs, onSnapshot, orderBy, query, setDoc, updateDoc } from 'firebase/firestore';
import { firestore, storage } from '../../firebase/firebaseConfig';
import { SendOutlined, UploadOutlined } from '@ant-design/icons';
import { ref, uploadBytes } from 'firebase/storage';

dayjs.extend(customParseFormat);

function Invoices() {
    const [messageApi, contextHolder] = message.useMessage();
    const [invoiceModal, setInvoiceModal] = useState(false);
    const [date, setDate] = useState("");
    const [search, setSearch] = useState("");
    const [invoices, setInvoices] = useState([]);
    const [clients, setClients] = useState({});
    const [filteredInfo, setFilteredInfo] = useState({});
    const [form] = Form.useForm();

    const handleOk = () => {
        form.validateFields().then(async values => {
            messageApi.loading("Adding the invoice...", 0);
            values.billingDate = values.billingDate?.format("D MMM YYYY"); // Format date before submission
            await setDoc(doc(firestore, "api_invoices", values.invoiceId), {
                amount: values.amount,
                billingDate: values.billingDate,
                companyGST: clients[values.companyId].gst,
                companyId: values.companyId,
                companyName: clients[values.companyId].name,
                invoice: values.invoice,
                status: "Unpaid"
            })
            await setDoc(doc(firestore, `api_users/${values.companyId}/invoices`, values.invoiceId), {
                invoice: values.invoice
            })
            await uploadBytes(ref(storage, `api_users/${values.companyId}/Invoices/${values.invoiceId}.pdf`), values.invoiceFile.file.originFileObj);
            form.resetFields();

            setInvoiceModal(false);
            messageApi.destroy();
            messageApi.success("The invoice has been added.");
        }).catch(() => {
            messageApi.destroy();
            messageApi.error("Failed to add invoice. Please try again.");
        });
    };

    const handleChange = (pagination, filters, sorter) => {
        setFilteredInfo(filters);
    };

    useEffect(() => {
        const onload = async () => {
            setClients({
                ...((await getDocs(collection(firestore, "api_users"))).docs.reduce((acc, user) => {
                    acc[user.id] = user.data();
                    return acc;
                }, {}))
            })
        }

        return onSnapshot(query(collection(firestore, "api_invoices"), orderBy("invoice", "desc")), snapshot => {
            messageApi.loading("Getting all invoices...", 0);
            setInvoices([...(snapshot.docs.map((invoice, index) => ({ id: invoice.id, ...invoice.data(), index: index + 1 })))])

            onload();
            messageApi.destroy();
            messageApi.success("All the invoices have been received.");
        }, () => {
            messageApi.destroy();
            messageApi.error("Error occurred while retrieving the invoices.");
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <div className='invoices-container'>
            {contextHolder}
            <Modal
                title="Invoice Entry"
                open={invoiceModal}
                onCancel={() => setInvoiceModal(false)}
                onOk={handleOk}
                okText="Submit"
            >
                <Form form={form} layout="vertical">
                    {/* Company Selection */}
                    <Form.Item
                        label="Select Company"
                        name="companyId"
                        rules={[{ required: true, message: "Please select a company" }]}
                    >
                        <Select placeholder="Choose a company">
                            {Object.entries(clients).map(([id, item]) => (
                                <Select.Option value={id}>{item.name}</Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    {/* Invoice ID */}
                    <Form.Item
                        label="Invoice ID"
                        name="invoiceId"
                        rules={[{ required: true, message: "Please enter invoice id" }]}
                    >
                        <Input placeholder="Enter invoice id" />
                    </Form.Item>

                    {/* Invoice Number */}
                    <Form.Item
                        label="Invoice Number"
                        name="invoice"
                        rules={[{ required: true, message: "Please enter invoice number" }]}
                    >
                        <Input placeholder="Enter invoice number" />
                    </Form.Item>

                    {/* Billing Date */}
                    <Form.Item
                        label="Billing Date"
                        name="billingDate"
                        rules={[{ required: true, message: "Please select billing date" }]}
                    >
                        <DatePicker format="D MMM YYYY" style={{ width: "100%" }} />
                    </Form.Item>

                    {/* Amount */}
                    <Form.Item
                        label="Amount"
                        name="amount"
                        rules={[{ required: true, message: "Please enter amount" }]}
                    >
                        <InputNumber placeholder="Enter amount" style={{ width: "100%" }} min={0} />
                    </Form.Item>

                    {/* Invoice PDF Upload */}
                    <Form.Item
                        label="Upload Invoice (PDF)"
                        name="invoiceFile"
                        rules={[{ required: true, message: "Please upload an invoice PDF" }]}
                    >
                        <Upload
                            beforeUpload={(file) => {
                                const isPDF = file.type === "application/pdf";
                                if (!isPDF) {
                                    message.error("You can only upload PDF files!");
                                }
                                return isPDF || Upload.LIST_IGNORE;
                            }}
                            maxCount={1}
                        >
                            <Button icon={<UploadOutlined />}>Upload PDF</Button>
                        </Upload>
                    </Form.Item>
                </Form>
            </Modal>
            <div className='invoices-header'>
                <h3>Invoices List</h3>
                <Space>
                    <DatePicker onChange={(event) => event ? setDate(event.format("MMM YYYY")) : setDate("")} format={"MMM YYYY"} picker='month' />
                    <Button onClick={() => setInvoiceModal(true)}>+ Add New Invoice</Button>
                </Space>
            </div>
            <div className='invoices-mid-container'>
                <div className='invoices-mid-item'>
                    <span>Total Invoices</span>
                    <span>{invoices.length}</span>
                </div>
                <div className='invoices-mid-item'>
                    <span>Revenue Generated</span>
                    <span>{invoices.reduce((sum, item) => sum + item.amount, 0).toLocaleString("en-IN", {
                        style: "currency",
                        currency: "INR"
                    })}</span>
                </div>
                <div className='invoices-mid-item'>
                    <span>Number of Clients</span>
                    <span>{Object.keys(clients).length}</span>
                </div>
                <div className='invoices-logo-container'>
                    <img src={require("../../assets/logo.png")} alt="logo" height={"100%"} width={"100%"} />
                </div>
            </div>
            <div className='invoices-table-container'>
                <Input placeholder='Search Invoices...' onChange={(event) => setSearch(event.target.value)} style={{ width: "300px", margin: "0.5rem 0 0 0.5rem" }}></Input>
                <Table scroll={{
                    y: document.getElementById("root").clientHeight * 0.55,
                }} pagination={{
                    defaultPageSize: 20,
                }} columns={[
                    {
                        title: '#',
                        dataIndex: 'index',
                        key: 'index',
                        ellipsis: true,
                        width: "90px",
                        align: "center"
                    },
                    {
                        title: 'Invoice Id',
                        dataIndex: 'invoice',
                        render: (text, object) => <a href={`https://firebasestorage.googleapis.com/v0/b/appyard-e37d6.appspot.com/o/api_users%2F${(object.companyId ?? "F7gIIYHHXoh0TyjoGF4lLK0CN6d2")}%2FInvoices%2F${object.id}.pdf?alt=media`} target="_blank" rel="noreferrer">{text}</a>,
                        key: 'invoice',
                        width: '100px'
                    },
                    {
                        title: 'Billing Date',
                        dataIndex: 'billingDate',
                        key: 'billingDate',
                        ellipsis: true,
                        width: '200px'
                    },
                    {
                        title: 'Company Name',
                        dataIndex: 'companyName',
                        key: 'companyName',
                        render: (text, object) => <span className=''>{text}</span>,
                        filters: Object.entries(clients).map(element => ({ text: element[1].name, value: element[1].name })),
                        filteredValue: filteredInfo.companyName || null,
                        onFilter: (value, record) => record.companyName.includes(value),
                        ellipsis: true,
                        width: "300px"
                    },
                    {
                        title: 'Company GST',
                        dataIndex: 'companyGST',
                        key: 'companyGST',
                        ellipsis: true,
                        width: "150px"
                    },
                    {
                        title: 'Amount',
                        dataIndex: 'amount',
                        key: 'amount',
                        render: (text) => <span>{text.toLocaleString("en-IN", {
                            style: "currency",
                            currency: "INR"
                        })}</span>,
                        ellipsis: true,
                        width: "120px"
                    },
                    {
                        title: 'Status',
                        dataIndex: 'status',
                        key: 'status',
                        render: (text, object) => <span className={text.toLowerCase()} onClick={async () => await updateDoc(doc(firestore, "api_invoices", object.id), {
                            status: text === "Paid" ? "Unpaid" : "Paid"
                        })}>{text}</span>,
                        filters: ["Paid", "Unpaid"].map(element => ({ text: element, value: element })),
                        filteredValue: filteredInfo.status || null,
                        onFilter: (value, record) => record.status?.includes(value),
                        ellipsis: true,
                        width: "100px"
                    },
                    {
                        title: 'Action',
                        dataIndex: 'send',
                        key: 'send',
                        align: "center",
                        render: (text, object) => <Button onClick={() => {
                        }}><SendOutlined /> Send</Button>,
                        width: "150px"
                    },
                ]} dataSource={invoices.filter((invoice => invoice.billingDate?.includes(date) && (invoice.invoice?.toLowerCase().includes(search.toLowerCase()) || invoice.companyGST?.toLowerCase().includes(search.toLowerCase()))))} onChange={handleChange}
                    rowHoverable={false} />
            </div>
        </div>
    )
}

export default Invoices
